import React, { FunctionComponent, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../../Component/Icon/Icon';
import { ShoppingPlannerContext } from '../../../Context/ShoppingPlannerContext';
import { VariantSelector, Variant } from '../VariantSelector/VariantSelector';
import { Skirting, SkirtingDecor } from '../../../Api/AccessoriesApi';
import { useProductsFromStates } from '../../../Hook/UseProductsFromStates';
import { SmallButton } from '../../../Component/SmallButton/SmallButton';
import { useResponsive } from '../../../Hook/UseResponsive';
import { AccessoriesPage } from '../AccessoriesPage';
import { ShoppingPlannerInput } from '../../../Component/ShoppingPlannerInput/ShoppingPlannerInput';
import { urls } from '../../../Constants/Urls';

import './SkirtingPage.scss';
import '../../../Component/SelectionMarker/SelectionMarker.scss';

export const SkirtingPage: FunctionComponent = () => {
    const { accessories, setSelectedSkirtingDecor, selectedSkirtingDecor } = useContext(ShoppingPlannerContext);

    // Variant Selector
    const [variants, setVariants] = useState<Variant[]>([]);

    const variantSelected = (variant: Variant) => variant.articleNumber === selectedSkirtingDecor;

    const closeAction = () => {
        setVariants([]);
    };

    const selectAction = (articleNumber: string) => {
        setSelectedSkirtingDecor(articleNumber);
    };

    const { isLargerThanMd } = useResponsive();

    const { decor, skirtingDecor } = useProductsFromStates();

    // find skirting that matches decor or fall back to skirting if no decors are present at all
    const matchingDecor = (skirting: Skirting) =>
        skirting.decors.find(
            (skirtingDecorFromList) =>
                skirtingDecor?.articleNumber === skirtingDecorFromList.articleNumber ||
                (decor && skirtingDecorFromList.decorNumbers.includes(decor.decorNumber))
        ) ??
        skirting.decors[0] ??
        // this fallback might not be needed if we make sure all skirting have at least one decor
        skirting;

    const selected = (skirtingDecorToCheck: SkirtingDecor) =>
        skirtingDecorToCheck.articleNumber === skirtingDecor?.articleNumber;

    return (
        <>
            <AccessoriesPage className="shopping-planner-skirting-page">
                {isLargerThanMd ? <SkirtingSidebar /> : null}
                <div className="shopping-planner__scroll-area">
                    <div className="shopping-planner-accessories-page__accessories">
                        {isLargerThanMd ? null : (
                            <h2 className="shopping-planner-accessories-page__headline">Leisten</h2>
                        )}
                        <div className="shopping-planner-accessories-page__breadcrumbs">
                            <Link
                                to={urls.steps.accessoriesInsulation.builder()}
                                className="shopping-planner-accessories-page__headline"
                            >
                                Dämmung
                            </Link>
                            <Icon icon="arrow" />
                            <h2 className="shopping-planner-accessories-page__headline">Leisten</h2>

                            {isLargerThanMd ? null : (
                                <>
                                    <Icon icon="arrow" />
                                    <Link
                                        to={urls.steps.accessoriesInstallation.builder()}
                                        className="shopping-planner-accessories-page__headline"
                                    >
                                        Verlegung & Pflege
                                    </Link>
                                </>
                            )}
                        </div>
                        {isLargerThanMd ? null : <SkirtingSidebar />}
                        <ul className="shopping-planner-skirtings">
                            {accessories.skirtings.map((skirting) => (
                                <li
                                    onClick={() => {
                                        setSelectedSkirtingDecor(
                                            matchingDecor(skirting).articleNumber === selectedSkirtingDecor
                                                ? ''
                                                : matchingDecor(skirting).articleNumber
                                        );
                                    }}
                                    key={skirting.skirtingId}
                                    className={[
                                        'shopping-planner-skirting',
                                        selected(matchingDecor(skirting)) ? 'shopping-planner-skirting--selected' : '',
                                    ].join(' ')}
                                >
                                    <img src={matchingDecor(skirting).imageUri ?? ''}></img>
                                    <div className="shopping-planner-skirting__description">
                                        <div className="shopping-planner-skirting__label">{skirting.label}</div>
                                        <div className="shopping-planner-skirting__dimensions">
                                            {skirting.dimensions.length} x {skirting.dimensions.height} x{' '}
                                            {skirting.dimensions.height} mm
                                        </div>
                                        {skirting.decors.length > 1 ? (
                                            <SmallButton
                                                className="shopping-planner-accessories-page__detail-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setVariants(skirting.decors);
                                                }}
                                            >
                                                Details
                                            </SmallButton>
                                        ) : null}
                                    </div>
                                    {selected(matchingDecor(skirting)) ? (
                                        <Icon icon="check" className="shopping-planner-selection-marker" />
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </AccessoriesPage>
            {variants.length > 1 ? (
                <VariantSelector
                    variants={variants}
                    selectAction={selectAction}
                    closeAction={closeAction}
                    isSelected={variantSelected}
                />
            ) : null}
        </>
    );
};

const SkirtingSidebar: FunctionComponent = (props) => {
    const { wallLength, setWallLength, corners, setCorners, innerCorners, setInnerCorners, endCaps, setEndCaps } =
        useContext(ShoppingPlannerContext);

    const { isLargerThanMd } = useResponsive();

    const [showInputs, setShowInputs] = useState(isLargerThanMd);

    return (
        <>
            <aside className="shopping-planner-sidebar">
                {isLargerThanMd ? null : (
                    <button
                        type="button"
                        className="shopping-planner-accessories-page__filter-button"
                        onClick={() => setShowInputs((currentShowInputs) => !currentShowInputs)}
                    >
                        Weitere Daten eingeben
                        <Icon icon="edit"></Icon>
                    </button>
                )}
                {showInputs ? (
                    <div className="shopping-planner-skirting-page__inputs">
                        <ShoppingPlannerInput
                            label="Gesamtlänge der Wände (m)"
                            value={wallLength}
                            onChange={(e) => setWallLength(e.currentTarget.value)}
                            grey={true}
                            type="number"
                        />
                        <ShoppingPlannerInput
                            label="Anzahl Außenecken"
                            value={corners}
                            onChange={(e) => setCorners(e.currentTarget.value)}
                            grey={true}
                            type="number"
                        />

                        <ShoppingPlannerInput
                            label="Anzahl Innenecken"
                            value={innerCorners}
                            onChange={(e) => setInnerCorners(e.currentTarget.value)}
                            grey={true}
                            type="number"
                        />
                        <ShoppingPlannerInput
                            label="Anzahl Endkappen"
                            value={endCaps}
                            onChange={(e) => setEndCaps(e.currentTarget.value)}
                            grey={true}
                            type="number"
                        />
                    </div>
                ) : null}
            </aside>
        </>
    );
};
